/*
  Editorial scroll motion — TWO independent, separately-toggled effects:

    1. MEDIA layer (img / video / curtain) → class .is-visible, ONE-SHOT.
       Per-media "Reveal on scroll" (adds .editorial-media--reveal). Fires once when the tile
       crosses the −20% line (IntersectionObserver); the clip-reveal + curtain play on CSS transition.

    2. BLOCK layer (the tile itself drifting up) → SCROLL-SCRUBBED, WITH LERP SMOOTHING.
       Section-level "Media smooth enter" (adds .editorial-media-group--smooth on the group), so it
       applies to EVERY media in that section — independent of each media's Reveal-on-scroll. Scroll
       position gives a TARGET translate (START_MOBILE / START_DESKTOP when the tile's top enters at the bottom → 0 when
       its top reaches COMPLETE_AT of viewport height). Each frame we ease current → target
       (current += (target−current)*ease) — the catch-up of GSAP scrub / Lenis `lerp`. Written inline;
       composes with the block's stagger `transform`.

  The rAF loop runs only while easing and auto-stops once settled; scroll/resize kicks it on. Reads
  (getBoundingClientRect) and writes (style.translate) are batched into separate passes (no thrash).

  Gated on <html class="editorial-anim"> so with NO JS media just shows. In the theme editor /
  reduced-motion we reveal media at rest and DON'T scrub.
*/
(() => {
  const REVEAL_SEL = ".editorial-media--reveal";
  const SCRUB_SEL = ".editorial-media-group--smooth .editorial-media";
  const revealMedia = (el) => el.classList.add("is-visible");

  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const editing = window.Shopify && window.Shopify.designMode;

  // Theme editor OR reduced motion: reveal media at rest, no clip-reveal, no scrub.
  if (editing || reduce) {
    const revealAll = () =>
      document.querySelectorAll(REVEAL_SEL).forEach(revealMedia);
    revealAll();
    if (editing) {
      document.addEventListener("shopify:section:load", revealAll);
      document.addEventListener("shopify:block:select", revealAll);
    }
    return; // block scrub is skipped here entirely
  }

  // ---- 1. MEDIA layer: one-shot clip-reveal at −20% ("Reveal on scroll") ----
  const reveal = [...document.querySelectorAll(REVEAL_SEL)];
  if (reveal.length) {
    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver(
        (entries, obs) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              revealMedia(entry.target);
              obs.unobserve(entry.target); // one-shot
            }
          }
        },
        { threshold: 0, rootMargin: "0px 0px -20% 0px" },
      );
      reveal.forEach((el) => io.observe(el));
    } else {
      reveal.forEach(revealMedia); // no IO → just show
    }
  }

  // ---- 2. BLOCK layer: scroll-scrubbed drift with lerp smoothing ("Media smooth enter") ----
  const scrubEls = [...document.querySelectorAll(SCRUB_SEL)];
  if (!scrubEls.length) return;

  // Drift distance, % of the tile's own height — separate values per breakpoint. ← tune here
  const START_DESKTOP = 2;
  const START_MOBILE = 7;
  const mqMobile = window.matchMedia("(max-width: 48rem)"); // matches the theme's stack breakpoint
  const startPct = () => (mqMobile.matches ? START_MOBILE : START_DESKTOP);

  const COMPLETE_AT = 0.55; // drift reaches rest when the tile's top hits this fraction of viewport height
  const EASE = 0.08; // catch-up per 60fps-frame (0..1). Lower = smoother/more lag, à la scrub: n.
  const FRAME = 1000 / 60;

  const state = scrubEls.map((el) => ({
    el,
    current: startPct(),
    target: startPct(),
    y: null,
  }));

  // PASS 1 (reads): map each tile's scroll position to its target translate.
  const measure = () => {
    const vh = window.innerHeight;
    const span = vh * COMPLETE_AT; // scroll distance (px) over which the drift completes
    const start = startPct(); // current breakpoint's drift distance
    for (const s of state) {
      const top = s.el.getBoundingClientRect().top;
      let p = (vh - top) / span; // 0 at viewport bottom, 1 at the COMPLETE_AT line
      s.target = start * (1 - (p < 0 ? 0 : p > 1 ? 1 : p));
    }
  };

  // PASS 2 (writes): ease current → target and write translate. Returns true if still moving.
  const apply = (factor) => {
    let moving = false;
    for (const s of state) {
      const diff = s.target - s.current;
      if (Math.abs(diff) > 0.01) {
        s.current += diff * factor;
        moving = true;
      } else {
        s.current = s.target;
      }
      const y = s.current.toFixed(2);
      if (s.y !== y) {
        s.el.style.translate = "0 " + y + "%";
        s.y = y;
      }
    }
    return moving;
  };

  let rafId = null;
  let last = 0;
  const tick = (now) => {
    const dt = last ? now - last : FRAME;
    last = now;
    // frame-rate-independent lerp factor, so 60Hz and 120Hz feel identical
    const factor = 1 - Math.pow(1 - EASE, dt / FRAME);
    measure();
    if (apply(factor)) {
      rafId = requestAnimationFrame(tick);
    } else {
      rafId = null; // settled → stop the loop
      last = 0;
    }
  };
  const kick = () => {
    if (rafId == null) rafId = requestAnimationFrame(tick);
  };

  window.addEventListener("scroll", kick, { passive: true });
  window.addEventListener("resize", kick, { passive: true });
  mqMobile.addEventListener("change", kick); // re-target when crossing the mobile/desktop breakpoint

  // Initial: snap to the scroll-correct position (no intro animation), then ease on later scroll.
  measure();
  for (const s of state) {
    s.current = s.target;
    s.y = s.current.toFixed(2);
    s.el.style.translate = "0 " + s.y + "%";
  }
})();
